import { useState, useEffect } from 'react';
import { FaShieldAlt, FaSave, FaCheck } from 'react-icons/fa';
import api from '../../api';

const ALL_PERMISSIONS = [
  { key: 'admin_access',              label: 'Admin Dashboard Access' },
  { key: 'manage_employees',          label: 'Manage Employees' },
  { key: 'manage_projects',           label: 'Manage Projects' },
  { key: 'manage_clients',            label: 'Manage Clients' },
  { key: 'manage_holidays',           label: 'Manage Holidays' },
  { key: 'manage_positions',          label: 'Manage Positions' },
  { key: 'manage_leaves',             label: 'Approve / Reject Leave Requests' },
  { key: 'review_timesheets',         label: 'Review Timesheets' },
  { key: 'view_reports',              label: 'View Reports & Analytics' },
  { key: 'manage_settings',           label: 'Manage Settings' },
  { key: 'manage_consultant_rates',   label: 'Manage Consultant Rates' },
  { key: 'generate_invoices',         label: 'Generate Invoices' },
  { key: 'manage_roles',              label: 'Manage Roles & Permissions' },
];

export default function AdminRoles() {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts]   = useState({});   // { roleName: { description, permissions[] } }
  const [saving, setSaving]   = useState({});
  const [saved, setSaved]     = useState({});
  const [error, setError]     = useState('');

  useEffect(() => {
    api.getRoles()
      .then(r => {
        setRoles(r);
        const d = {};
        r.forEach(role => { d[role.name] = { description: role.description, permissions: [...role.permissions] }; });
        setDrafts(d);
      })
      .catch(() => setError('Failed to load roles.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (roleName, perm) => {
    setDrafts(prev => {
      const perms = prev[roleName].permissions;
      return {
        ...prev,
        [roleName]: {
          ...prev[roleName],
          permissions: perms.includes(perm) ? perms.filter(p => p !== perm) : [...perms, perm],
        },
      };
    });
  };

  const handleSave = async (role) => {
    setSaving(s => ({ ...s, [role.name]: true }));
    setError('');
    try {
      await api.updateRolePermissions(role.name, drafts[role.name]);
      setSaved(s => ({ ...s, [role.name]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [role.name]: false })), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(s => ({ ...s, [role.name]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#0c1b4d] flex items-center justify-center">
            <FaShieldAlt className="text-[#D4AF37]" size={16} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0c1b4d]">Roles &amp; Permissions</h1>
            <p className="text-xs text-gray-500">Changes take effect on the user's next login</p>
          </div>
        </div>

        {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : (
          <div className="space-y-6">
            {roles.map(role => (
              <div key={role.name} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <div>
                    <h2 className="font-bold text-[#0c1b4d] capitalize">{role.name}</h2>
                    <input
                      value={drafts[role.name]?.description || ''}
                      onChange={e => setDrafts(prev => ({ ...prev, [role.name]: { ...prev[role.name], description: e.target.value } }))}
                      className="mt-1 text-xs text-gray-500 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-[#0c1b4d] w-72"
                      placeholder="Role description…"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    {saved[role.name] && (
                      <span className="text-xs text-emerald-600 flex items-center gap-1 font-medium">
                        <FaCheck size={11} /> Saved
                      </span>
                    )}
                    <button
                      onClick={() => handleSave(role)}
                      disabled={saving[role.name]}
                      className="flex items-center gap-2 px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors disabled:opacity-50"
                    >
                      <FaSave size={12} /> {saving[role.name] ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map(({ key, label }) => {
                    const checked = drafts[role.name]?.permissions?.includes(key) || false;
                    return (
                      <label key={key} className="flex items-center gap-3 py-1.5 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(role.name, key)}
                          className="w-4 h-4 rounded border-gray-300 text-[#0c1b4d] focus:ring-[#0c1b4d]"
                        />
                        <span className={`text-sm ${checked ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                          {label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
